import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faClose } from '@fortawesome/sharp-solid-svg-icons';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import ButtonNavigation from '@/components/buttonNavigation';
import Typography from '@/components/typography';
import { hasUserHiddenSeoBanner } from '@/constants/localStorage';
import {
  COMPS_URL,
  LISTINGS_URL,
  PLAT_MAP_URL_LANDING,
  PRICING_URL,
} from '@/constants/urls';
import { County as GetCountiesCounty } from '@/endpoints/itasca/county/getCounties';
import { AdjacentCounty, County } from '@/endpoints/itasca/county/getCounty';
import { State } from '@/endpoints/itasca/state/getState';
import { useNavigationContext } from '@/features/navigation/provider';
import useWebStorage from '@/hooks/useWebStorage';
import { useAuth } from '@/providers/auth-provider';

import { BreadCrumb } from './breadcrumb';
import { StatesAccordians } from './statesAccordians';

export type CuratedCompSeoExpansion = {
  county_name: string;
  county_state_slug: string;
};

interface Props {
  title?: string;
  body?: string;
  serverSide?: boolean;
  adjacentCounties?: AdjacentCounty[];
  stateCounties?: GetCountiesCounty[];
  stateInfo?: State;
  countyInfo?: County;
  countiesWithListings: any[];
  countiesWithSoldLand?: CuratedCompSeoExpansion[];
  className?: string;
}

const salesPitch = (
  <>
    Ready to unlock nationwide plat maps? Acres brings together&nbsp;
    <Link
      href="https://landvalues.acres.com/how-buy-land-step-by-step-guide/"
      target="_blank"
    >
      land for sale
    </Link>
    ,&nbsp;
    <Link href="https://landvalues.acres.com/tag/trends" target="_blank">
      sold land
    </Link>
    , and&nbsp;
    <Link href="https://support.acres.com/use-insight-layers" target="_blank">
      parcel-level insights
    </Link>{' '}
    into one GIS mapping platform for $7.49.
  </>
);

const SeoExpansion: React.FC<Props> = ({
  title = '',
  body = '',
  adjacentCounties = undefined,
  serverSide = false,
  stateCounties = [],
  stateInfo = undefined,
  countyInfo = undefined,
  countiesWithListings = [],
  countiesWithSoldLand = [],
  className = '',
}: Props) => {
  const { savedValue: hideExpansion, updateValue: setHideExpansion } =
    useWebStorage<boolean>({ key: hasUserHiddenSeoBanner });

  const { downloadAppCTAIsOpen } = useNavigationContext();

  const pathname = usePathname();
  const { isAnonymous } = useAuth();

  const isBaseMap = useMemo(() => {
    return (
      pathname?.includes(PLAT_MAP_URL_LANDING) && !countyInfo && !stateInfo
    );
  }, [countyInfo, pathname, stateInfo]);

  const counties = useMemo(() => {
    if (adjacentCounties && adjacentCounties.length > 0) {
      return adjacentCounties;
    }
    return stateCounties;
  }, [adjacentCounties, stateCounties]);

  const countiesText = useMemo(() => {
    if (adjacentCounties && adjacentCounties.length > 0) {
      return 'Adjacent Counties';
    }

    if (stateInfo?.name) {
      return `All ${stateInfo.name} Counties`;
    }
    return '';
  }, [adjacentCounties, stateInfo?.name]);

  const stateListingsText = useMemo(() => {
    if (stateInfo && countiesWithListings.length > 0) {
      return `All ${stateInfo.name} Counties with Listings`;
    }

    return '';
  }, [countiesWithListings, stateInfo]);

  const stateCompsText = useMemo(() => {
    if (stateInfo && countiesWithSoldLand.length > 0) {
      return `All ${stateInfo.name} Counties with Curated Sold Land`;
    }

    return '';
  }, [countiesWithSoldLand.length, stateInfo]);

  const countyListingsText = useMemo(() => {
    if (countyInfo) {
      return `${countyInfo.full_name} Listings`;
    }

    return '';
  }, [countyInfo]);

  const countySoldLandText = useMemo(() => {
    if (countyInfo) {
      return `${countyInfo.full_name} Sold Land`;
    }

    return '';
  }, [countyInfo]);

  const stateText = useMemo(() => {
    if (stateInfo) {
      return stateInfo.name;
    }

    if (countyInfo) {
      return countyInfo.state_name;
    }

    return 'States';
  }, [countyInfo, stateInfo]);

  const stateUrl = useMemo(() => {
    if (countyInfo) {
      return `${PLAT_MAP_URL_LANDING}/${countyInfo.state_abbrev.toLowerCase()}`;
    }

    if (!stateInfo && !countyInfo) {
      return `/directory`;
    }
    return undefined;
  }, [countyInfo, stateInfo]);

  const countyText = useMemo(() => {
    return countyInfo?.full_name;
  }, [countyInfo]);

  if (!title || !body || !isAnonymous || hideExpansion) return null;

  if (serverSide) {
    return (
      <div className="absolute top-2 z-[-1] hidden h-full w-full max-w-lg space-y-4 bg-white p-4 text-gray-100">
        <h1>{title}</h1>
        <p>{body}</p>
        <p>{salesPitch}</p>
      </div>
    );
  }

  return (
    <ButtonNavigation
      initializeOpen
      adjacentButtonToExpand={
        <Button
          size={ButtonSize.XS}
          variant={ButtonVariant.GHOST}
          onClick={() => setHideExpansion(true)}
        >
          <FontAwesomeIcon
            className="text-[16px] text-gray-70"
            icon={faClose}
          />
        </Button>
      }
      className={twMerge(
        'z-[161] max-w-[264px]',
        isMobile
          ? 'max-h-[calc(100vh-100px)]'
          : 'max-h-[calc(100vh - 200px)] pb-[75px]',
        className
      )}
      isExpandable={false}
      parentButtonClassName="bg-white sticky top-0 z-[201]"
      rows={[
        <div key="seo" className="items-end bg-gray-10 p-4 md:items-start">
          <div className="flex grow flex-col items-end">
            <div className="flex flex-col">
              <div className="space-y-2 text-sm text-gray-100 sm:flex-grow">
                <BreadCrumb
                  urls={[
                    {
                      text: stateText,
                      url: stateUrl,
                    },
                    {
                      text: countyText,
                    },
                  ]}
                />
                {stateInfo && (
                  <Typography className="my-6 text-gray-100" variant="h4">
                    {stateInfo.name} | {counties.length} Counties
                  </Typography>
                )}
                <p>{body}</p>
                <p>{salesPitch}</p>
              </div>
              {counties && counties.length > 0 && (
                <div>
                  <div className="flex gap-x-2">
                    <a
                      className="mt-4 text-green-50 underline hover:text-green-60"
                      href={`${LISTINGS_URL}/${countyInfo?.state_abbrev.toLowerCase()}/${
                        countyInfo?.county_state_slug
                      }`}
                    >
                      {countyListingsText}
                    </a>

                    <a
                      className="mt-4 text-green-50 underline hover:text-green-60"
                      href={`${COMPS_URL}/${countyInfo?.state_abbrev.toLowerCase()}/${
                        countyInfo?.county_state_slug
                      }`}
                    >
                      {countySoldLandText}
                    </a>
                  </div>

                  <Typography className="my-3 text-gray-100" variant="h5">
                    {countiesText}
                  </Typography>
                  <div className="flex flex-wrap gap-x-3">
                    {counties.map((c) => (
                      <a
                        key={c.full_name}
                        className=" text-green-50 underline hover:text-green-60"
                        href={`${PLAT_MAP_URL_LANDING}/${c.state_abbrev.toLowerCase()}/${
                          c.county_state_slug
                        }`}
                      >
                        {c.full_name}
                        {!stateInfo ? `, ${c.state_abbrev}` : null}
                      </a>
                    ))}
                  </div>
                  {stateInfo && (
                    <>
                      <Typography className="my-3 text-gray-100" variant="h5">
                        {stateListingsText}
                      </Typography>
                      <div className="flex flex-wrap gap-x-3">
                        {countiesWithListings?.map((c) => (
                          <a
                            key={c.county_name}
                            className=" text-green-50 underline hover:text-green-60"
                            href={`${LISTINGS_URL}/${stateInfo.state_abbrev.toLowerCase()}/${
                              c.county_state_slug
                            }`}
                          >
                            {c.county_name}
                          </a>
                        ))}
                      </div>
                      {countiesWithSoldLand &&
                        countiesWithSoldLand.length > 0 && (
                          <>
                            <Typography
                              className="my-3 text-gray-100"
                              variant="h5"
                            >
                              {stateCompsText}
                            </Typography>
                            <div className="flex flex-wrap gap-x-3">
                              {countiesWithSoldLand?.map((c) => (
                                <a
                                  key={c.county_state_slug}
                                  className=" capitalize text-green-50 underline hover:text-green-60"
                                  href={`${COMPS_URL}/${stateInfo.state_abbrev.toLowerCase()}/${
                                    c.county_state_slug
                                  }`}
                                >
                                  {c.county_name}
                                </a>
                              ))}
                            </div>
                          </>
                        )}
                    </>
                  )}
                </div>
              )}

              {isBaseMap && isMobile && (
                <div className="flex w-full flex-col overflow-scroll sm:hidden">
                  <StatesAccordians />
                </div>
              )}
            </div>

            <div
              className={twMerge(
                'fixed  w-[246px]',
                // eslint-disable-next-line no-nested-ternary
                isMobile
                  ? downloadAppCTAIsOpen
                    ? 'bottom-12 left-2'
                    : 'bottom-20 left-2'
                  : 'bottom-2 right-2'
              )}
            >
              <a
                className="text-white"
                href={PRICING_URL}
                rel="noreferrer"
                target="_blank"
              >
                <Button
                  className="w-full"
                  size={ButtonSize.LARGE}
                  variant={ButtonVariant.GRADIENT}
                >
                  <div className="flex items-center">
                    <span>Compare Plans</span>
                    <FontAwesomeIcon
                      className="ml-3 text-white"
                      icon={faArrowRight}
                    />
                  </div>
                </Button>
              </a>
            </div>
          </div>
        </div>,
      ]}
      title={
        <Typography className="my-2 mr-2 text-[16px]" variant="h1">
          {title}
        </Typography>
      }
    />
  );
};

export default SeoExpansion;
